import { batch, Component, createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { UploadState } from "./SaveUpload";
import { encodeREPOSave, healthUpgradeFullName, knownPlayerUpgradeToItemMapping, RepoSave, saveLevels } from "../lib/repoSaveLib";
import { createStore } from "solid-js/store";
import { FaRegularTrashCan, FaSolidMinus, FaSolidPlus } from "solid-icons/fa";
import { CgArrowsExchange } from "solid-icons/cg";
import { getPlayer } from "./PlayerSelector";
import { BiRegularSave, BiSolidDownload } from "solid-icons/bi";

const upgradeNamePrefix = 'playerUpgrade'
const upgradeItemNamePrefixes = ['Item Upgrade Player ', 'Item Upgrade '].sort((a, b) => b.length - a.length)

function getUpgradeItemShortName(upgradeItemName: string): string {
    const longestMatchedPrefix = upgradeItemNamePrefixes.find(prefix => upgradeItemName.startsWith(prefix))
    if(longestMatchedPrefix === undefined) {
        return upgradeItemName
    } else {
        return upgradeItemName.substring(longestMatchedPrefix.length)
    }
}

function titleCaseToSpaces(title: string): string {
    return [...title].map((c, i) => (i === 0 || c !== c.toLocaleUpperCase()) ? c : ' ' + c).join('')
}

type Upgrade = {
    upgradeName: string
    upgradeDisplayName: string
    upgradeFullName: string
    itemName: string
    itemFullName: string
}

type EditorProps = {
    state: UploadState
}

const Editor: Component<EditorProps> = (props) => {
    const [saved, setSaved] = createSignal(false)

    // Go through a lot of trouble to match upgrade names to item names
    // I could rely on hardcoded maps (like in `repoSaveLib.ts`), but I want to at least have a chance of supporting future updates without pushing a new webapp version
    const upgrades = createMemo<Upgrade[]>(() => {
        const data = props.state.state.data
        const dicts = data.dictionaryOfDictionaries.value
        return Object.keys(dicts)
            .filter(k=> k.startsWith(upgradeNamePrefix))
            .map<Upgrade | undefined>(k => {
                const upgradeName = k.substring(upgradeNamePrefix.length)
                // I miss using statements as expressions in rust, now I have to use IIFEs for immutable defs 😔
                const itemName = ((): {full: string, short: string} | undefined => {
                    if(knownPlayerUpgradeToItemMapping.hasOwnProperty(upgradeName)) {
                        const knownName = knownPlayerUpgradeToItemMapping[upgradeName]
                        return {
                            full: knownName,
                            short: getUpgradeItemShortName(knownName)
                        }
                    }

                    const upgradeItems = Object.entries(dicts.itemsUpgradesPurchased).map(([itemKey, itemCount]) => {
                        const shortName = getUpgradeItemShortName(itemKey)
                        return {
                            name: shortName,
                            fullName: itemKey,
                            count: itemCount,
                            possibleUpgradeName: shortName.replaceAll(' ', '')
                        }
                    })
                    const matchingUpradeItem = upgradeItems.find(item => item.possibleUpgradeName === upgradeName)
                    if(matchingUpradeItem !== undefined) {
                        console.warn(`Guessing item<->upgrade pairing! Item: "${matchingUpradeItem.fullName}", Upgrade: ${k}`)
                        return {
                            full: matchingUpradeItem.fullName,
                            short: matchingUpradeItem.name
                        }
                    } else {
                        console.warn(`No matching item found for upgrade "${k}"!`)
                        return undefined
                    }
                })()
                if(itemName === undefined) return undefined
                return {
                    upgradeName,
                    upgradeDisplayName: titleCaseToSpaces(upgradeName),
                    upgradeFullName: k,
                    itemName: itemName.short,
                    itemFullName: itemName.full
                }
            })
            .filter(v => v !== undefined)
    })

    const originalSave = createMemo(() => props.state.state.data)
    const [modifiedSave, setModifiedSave] = createStore<RepoSave>(structuredClone(originalSave()))
    createEffect(() => setModifiedSave(structuredClone(originalSave())))

    const upgradesInTruck = createMemo(() => {
        const itemFullNameToUpgradeMap = upgrades().reduce((prev, curr) => {
            prev.set(curr.itemFullName, curr)
            return prev
        }, new Map<string, Upgrade>())
        return Object.entries(modifiedSave.dictionaryOfDictionaries.value.itemsPurchased).reduce<{upgrade: Upgrade, count: number}[]>((acc, [itemFullName, count]) => {
            const upgrade = itemFullNameToUpgradeMap.get(itemFullName)
            if(upgrade !== undefined) acc.push({upgrade, count})
            return acc
        }, []).sort((a, b) => a.upgrade.itemName.localeCompare(b.upgrade.itemName))
    })

    const saveChanges = async (handle: FileSystemFileHandle) => {
        const encoded = await encodeREPOSave(modifiedSave)
        const writable = await handle.createWritable()
        await writable.write(encoded)
        await writable.close()
        
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
    }

    const downloadSave = async () => {
        const encoded = await encodeREPOSave(modifiedSave)
        const anchor = document.createElement('a')
        anchor.style.display = 'none'
        anchor.download = props.state.state.fileMeta.name
        const blob = new Blob([encoded], {type: 'octet/stream'})
        const blobUrl = URL.createObjectURL(blob)
        anchor.href = blobUrl

        document.body.appendChild(anchor)
        anchor.click()

        URL.revokeObjectURL(blobUrl)
        document.body.removeChild(anchor)
    }

    const getTruckUpgradeCount = (upgradeItemFullName: string) => {
        return modifiedSave.dictionaryOfDictionaries.value.itemsPurchased[upgradeItemFullName] ?? 0
    }

    const setTruckUpgradeCount = (upgradeItemFullName: string, amount: number) => {
        setModifiedSave('dictionaryOfDictionaries', 'value', 'itemsPurchased', upgradeItemFullName, amount === 0 ? undefined! : amount)
    }

    const getPlayerUpgradeCount = (playerID: string, upgradeFullName: string) => {
        return modifiedSave.dictionaryOfDictionaries.value[upgradeFullName][playerID] ?? 0
    }

    const setPlayerUpgradeCount = (playerID: string, upgradeFullName: string, amount: number) => {
        setModifiedSave('dictionaryOfDictionaries', 'value', upgradeFullName, playerID, amount === 0 ? undefined! : amount)
    }

    const removePlayer = (playerID: string) => {
        batch(() => {
            for(const upgrade of upgrades()) {
                setTruckUpgradeCount(upgrade.itemFullName, getTruckUpgradeCount(upgrade.itemFullName) + getPlayerUpgradeCount(playerID, upgrade.upgradeFullName))
                setPlayerUpgradeCount(playerID, upgrade.upgradeFullName, 0)
            }
            setModifiedSave('playerNames', 'value', playerID, undefined!)
            setModifiedSave('dictionaryOfDictionaries', 'value', 'playerHealth', playerID, undefined!)
        })
    }

    const addPlayer = async () => {
        const playerIDs = new Set(Object.keys(modifiedSave.playerNames.value))
        const player = await getPlayer((p) => !playerIDs.has(p.id))
        if(player !== undefined) {
            batch(() => {
                setModifiedSave('playerNames', 'value', player.id, player.name)
                setModifiedSave('dictionaryOfDictionaries', 'value', 'playerHealth', player.id, 100)
            })
            
        }
    }

    const swapPlayer = async (prevPlayerID: string) => {
        const playerIDs = new Set(Object.keys(modifiedSave.playerNames.value))
        const player = await getPlayer((p) => !playerIDs.has(p.id))
        if(player === undefined) return

        batch(() => {
            setModifiedSave('playerNames', 'value', player.id, player.name)
            setModifiedSave('dictionaryOfDictionaries', 'value', 'playerHealth', player.id, modifiedSave.dictionaryOfDictionaries.value.playerHealth[prevPlayerID] ?? 100)
            for(const upgrade of upgrades()) {
                setPlayerUpgradeCount(player.id, upgrade.upgradeFullName, getPlayerUpgradeCount(prevPlayerID, upgrade.upgradeFullName))
                setPlayerUpgradeCount(prevPlayerID, upgrade.upgradeFullName, 0)
            }
            setModifiedSave('playerNames', 'value', prevPlayerID, undefined!)
            setModifiedSave('dictionaryOfDictionaries', 'value', 'playerHealth', prevPlayerID, undefined!)
        })
    }

    const getPlayerMaxHealth = (playerID: string) => {
        return 100 + (getPlayerUpgradeCount(playerID, healthUpgradeFullName) * 20)
    }

    return <>
        <div class="space-y-4">
            <h3 class="text-2xl font-semibold underline underline-offset-8">Save Info</h3>
            <ul class="flex flex-row flex-wrap gap-8 justify-center text-2xl">
                <li>
                    <div>Next Level</div>
                    <div>{modifiedSave.dictionaryOfDictionaries.value.runStats.level + 1}</div>
                </li>
                <li>
                    <div>Current Money</div>
                    <div>${modifiedSave.dictionaryOfDictionaries.value.runStats.currency}K</div>
                </li>
                <li>
                    <div>Total Haul</div>
                    <div>${modifiedSave.dictionaryOfDictionaries.value.runStats.totalHaul}K</div>
                </li>
                <li>
                    <div>Start In</div>
                    <select
                        class="select w-full select-lg"
                        value={modifiedSave.dictionaryOfDictionaries.value.runStats["save level"]}
                        onInput={e => setModifiedSave('dictionaryOfDictionaries', 'value', 'runStats', 'save level', Number(e.currentTarget.value))}
                    >
                        <option value={saveLevels.level}>Level</option>
                        <option value={saveLevels.shop}>Shop</option>
                        <option value={saveLevels.truck}>Truck</option>
                    </select>
                </li>
            </ul>
            <Show when={modifiedSave.dictionaryOfDictionaries.value.runStats["save level"] === saveLevels.level && originalSave().dictionaryOfDictionaries.value.runStats["save level"] === saveLevels.shop}>
                <div class="alert alert-warning w-fit mx-auto">
                    Are you sure you want to skip the shop?
                </div>
            </Show>
            <Show when={modifiedSave.dictionaryOfDictionaries.value.runStats["save level"] === saveLevels.shop && originalSave().dictionaryOfDictionaries.value.runStats["save level"] === saveLevels.level}>
                <div class="alert alert-info alert-outline w-fit mx-auto">
                    Don't forget to immediately exit the shop, unless you want to cheat with two shops ;)
                </div>
            </Show>

            <h3 class="text-2xl font-semibold underline underline-offset-8">Upgrades in Truck</h3>
            <ul class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                <For each={upgrades().sort((a, b) => a.itemName.localeCompare(b.itemName))}>
                    {(upgrade) => <li
                        class="w-full text-lg text-left"
                        classList={{
                            'font-light': getTruckUpgradeCount(upgrade.itemFullName) === 0,
                            'font-bold': getTruckUpgradeCount(upgrade.itemFullName) !== 0
                        }}
                    >
                        {getTruckUpgradeCount(upgrade.itemFullName)} {upgrade.itemName}
                    </li>}
                </For>
            </ul>

            <h3 class="text-2xl font-semibold underline underline-offset-8">Players</h3>
            <button class="btn btn-primary" onClick={addPlayer}>
                <FaSolidPlus size={12}/> Add Player
            </button>
            <ul class="flex flex-col gap-4 justify-center text-xl">
                <For each={Object.entries(modifiedSave.playerNames.value)}>
                    {([playerID, name]) => <li class="card bg-base-300 dark:bg-neutral shadow-sm">
                        <div class="card-body">
                            <div class="self-start text-left flex flex-row w-full items-center">
                                <div class="grow flex flex-row items-center gap-4">
                                    <div>
                                        <div class="font-semibold text-xl">{name}</div>
                                        <pre class="block text-sm font-light">{playerID}</pre>
                                    </div>
                                    <div class="text-xl">
                                        {modifiedSave.dictionaryOfDictionaries.value.playerHealth[playerID] ?? getPlayerMaxHealth(playerID)} / {getPlayerMaxHealth(playerID)} health
                                    </div>
                                </div>
                                <div>
                                    <button class="btn btn-square hover:btn-error" title="Remove player and add upgrades to truck" onClick={() => removePlayer(playerID)}>
                                        <FaRegularTrashCan />
                                    </button>
                                    <button class="btn btn-square hover:btn-info ml-2" title="Swap player" onClick={() => swapPlayer(playerID)}>
                                        <CgArrowsExchange size={18}/>
                                    </button>
                                </div>
                            </div>

                            <ul class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                <For each={upgrades().sort((a, b) => a.itemName.localeCompare(b.itemName))}>
                                    {(upgrade => {
                                        const appliedCount = createMemo(() => getPlayerUpgradeCount(playerID, upgrade.upgradeFullName))
                                        const truckCount = createMemo(() => getTruckUpgradeCount(upgrade.itemFullName))

                                        const addUpgrade = () => {
                                            batch(() => {
                                                setTruckUpgradeCount(upgrade.itemFullName, truckCount() - 1)
                                                setPlayerUpgradeCount(playerID, upgrade.upgradeFullName, appliedCount() + 1)
                                                if(upgrade.upgradeFullName === healthUpgradeFullName) {
                                                    setModifiedSave('dictionaryOfDictionaries', 'value', 'playerHealth', playerID, existing => existing + 20)
                                                }
                                            })
                                        }

                                        const removeUpgrade = () => {
                                            batch(() => {
                                                setTruckUpgradeCount(upgrade.itemFullName, truckCount() + 1)
                                                setPlayerUpgradeCount(playerID, upgrade.upgradeFullName, appliedCount() - 1)
                                                if(upgrade.upgradeFullName === healthUpgradeFullName) {
                                                    setModifiedSave('dictionaryOfDictionaries', 'value', 'playerHealth', playerID, existing => Math.max(1, existing - 20))
                                                }
                                            })
                                        }

                                        return <li
                                            class="text-left flex flex-row w-full items-center text-base"
                                            classList={{
                                                'font-light': appliedCount() === 0,
                                                'font-bold': appliedCount() !== 0
                                            }}
                                        >
                                            <div class="w-20">
                                                <button
                                                    class="btn btn-xs btn-error btn-square"
                                                    disabled={appliedCount() === 0}
                                                    onClick={removeUpgrade}
                                                >
                                                    <FaSolidMinus size={12}/>
                                                </button>
                                                <span class="mx-1">{appliedCount()}</span>
                                                <button
                                                    class="btn btn-xs btn-success btn-square"
                                                    disabled={truckCount() === 0}
                                                    onClick={addUpgrade}
                                                >
                                                    <FaSolidPlus size={12}/>
                                                </button>
                                            </div>
                                            {upgrade.itemName}
                                        </li>
                                    })}
                                </For>
                            </ul>
                        </div>
                    </li>}
                </For>
            </ul>

            <div>
                <Show when={props.state.handle !== undefined && props.state.handle}>
                    {(handle) => <>
                        <button class="btn btn-success mr-2" onClick={() => saveChanges(handle())}>
                            <BiRegularSave size={18}/> Save Changes
                        </button>
                        <Show when={saved()}>
                            <p class="text-green-300">Saved!</p>
                        </Show>
                    </>}
                </Show>
                <button class="btn btn-success" onClick={downloadSave}>
                    <BiSolidDownload size={18}/> Download Save
                </button>
            </div>
        </div>
    </>
}

export default Editor